<script setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  inject,
  nextTick,
} from 'vue';
import { useEventListener } from '@vueuse/core';
import { isEmpty, includes } from 'lodash';
import estructuraEscolar from '@/api/estructura_escolar';
import SelectorEstructuraDisplay from './selector_estructura/SelectorEstructuraDisplay.vue';
import SelectorEstructuraModal from './selector_estructura/SelectorEstructuraModal.vue';
import {
  TIPOS,
  TIPO_HIJOS_KEY,
  labels,
} from './selector_estructura/constantes';

const props = defineProps({
  tiposSeleccionables: { type: Array, default: () => ['anno_lectivo', 'ensennanza', 'curso', 'modalidad', 'grupo'] },
  tiposEnsennanzaPermitidos: { type: Array, default: null },
  valoresIniciales: { type: Object, default: null },
  namespaceParametros: { type: [String, Boolean], default: 'estructura' },
  soloAnnoActual: { type: Boolean, default: false },
  idAnnoLectivo: { type: [String, Number, Array], default: null },
  disabled: { type: Boolean, default: false },
  submitAlCambiar: { type: [Boolean, String], default: false },
  nivelModal: { type: Number, default: 2 },
});

const I18n = inject('I18n');

const OTROS_GRUPOS_LABEL = I18n.t('Grupos');

const emit = defineEmits(['seleccionada']);

const wrapper = ref(null);
const modalAbierto = ref(false);
const loading = ref(false);
const arbolEstructura = ref([]);
const pathSeleccionado = ref([]);
const seleccionConfirmada = ref([]);
const columnas = ref([]);
const error = ref(null);
const inicializado = ref(false);

const tiposPermitidos = computed(() => props.tiposSeleccionables || TIPOS);

function nivelDebeOcultarse(nivelNode, ensennanzaNode) {
  // Ocultar si tiene el mismo nombre que la enseñanza (case insensitive)
  const nombreNivel = (nivelNode.nombre || '').toLowerCase().trim();
  const nombreEnsennanza = (ensennanzaNode?.nombre || '').toLowerCase().trim();
  if (nombreNivel && nombreNivel === nombreEnsennanza) return true;

  // Ocultar si hay solo un nivel bajo su enseñanza
  if (ensennanzaNode) {
    const ensennanzaEnArbol = arbolEstructura.value
      .flatMap((anno) => anno.ensennanzas || [])
      .find((e) => e.id === ensennanzaNode.id);
    if (ensennanzaEnArbol) {
      return (ensennanzaEnArbol.niveles?.length || 0) <= 1;
    }
  }
  return false;
}

const nodosSeleccionMostrados = computed(() => {
  if (!seleccionConfirmada.value.length) return [];
  const ensennanzaNode = seleccionConfirmada.value.find((n) => n.type === 'ensennanza');
  return seleccionConfirmada.value.filter((node) => {
    if (node.type !== 'nivel') return true;
    return !nivelDebeOcultarse(node, ensennanzaNode);
  });
});

// Puede no ser el último elemento del path si hay nodos intermedios auto-seleccionados
// (ej. nivel único insertado automáticamente entre enseñanza y curso)
const ultimoNodoSeleccionable = computed(
  () => [...pathSeleccionado.value]
    .reverse()
    .find((node) => includes(tiposPermitidos.value, node.type)),
);

const puedeAceptar = computed(() => Boolean(ultimoNodoSeleccionable.value));

const valoresCampos = computed(() => {
  const valores = {};
  const path = seleccionConfirmada.value || [];

  tiposPermitidos.value.forEach((tipo) => {
    const nodo = path.find((node) => node.type === tipo);
    valores[tipo] = nodo ? nodo.id : '';
  });

  return valores;
});

function nombreCampo(tipo) {
  if (props.namespaceParametros === false || props.namespaceParametros === null) return null;
  if (props.namespaceParametros) return `${props.namespaceParametros}[${tipo}_id]`;
  return `${tipo}_id`;
}

function getPathSeleccionadoEnHash(options = {}) {
  const hash = {};
  const path = seleccionConfirmada.value || [];

  path.forEach((node) => {
    if (options.soloTiposPermitidos !== true || includes(tiposPermitidos.value, node.type)) {
      hash[node.type] = node;
    }
  });

  return hash;
}

function crearNodoOtrosGrupos(parentNode, gruposDirectos = []) {
  return {
    id: `otros-grupos-${parentNode.id}`,
    type: 'otros_grupos',
    nombre: OTROS_GRUPOS_LABEL,
    etiqueta: OTROS_GRUPOS_LABEL,
    __otrosGrupos: gruposDirectos.map((group) => ({ ...group, type: group.type || 'grupo' })),
  };
}

function obtenerHijos(node) {
  if (!node) return [];
  if (node.__otrosGrupos) return node.__otrosGrupos;
  const childrenKey = TIPO_HIJOS_KEY[node.type];
  const items = (childrenKey && node[childrenKey]) || [];
  // Grupos directos pueden aparecer en cualquier nodo a partir de enseñanza.
  // modalidad ya los obtiene como hijos normales vía TIPO_HIJOS_KEY.
  const gruposDirectos = (node.grupos || []).map((group) => ({ ...group, type: group.type || 'grupo' }));
  if (node.type === 'anno_lectivo' || node.type === 'modalidad') return items;
  if (!isEmpty(items) && !isEmpty(gruposDirectos)) {
    return [...items, crearNodoOtrosGrupos(node, gruposDirectos)];
  }
  return isEmpty(gruposDirectos) ? items : [...items, ...gruposDirectos];
}

function construirPathFromHash(hash) {
  if (!hash || typeof hash !== 'object') return [];
  return TIPOS.filter((tipo) => hash[tipo]).map((tipo) => ({
    ...hash[tipo],
    type: tipo,
  }));
}

function tipoParaItems(items, tipoPadre) {
  const tipos = [...new Set(items.map((item) => item.type || tipoPadre))];
  return tipos.length === 1 ? tipos[0] : `${tipoPadre}_children`;
}

// Devuelve true si el tipo es seleccionable o si tiene descendientes seleccionables
// (es decir, si vale la pena mostrarlo para navegar hacia tipos permitidos)
function esNavegableOSeleccionable(itemOrTipo) {
  const tipo = typeof itemOrTipo === 'string' ? itemOrTipo : itemOrTipo?.type;
  if (!tipo) return false;
  if (tipo === 'otros_grupos') {
    return includes(tiposPermitidos.value, 'grupo');
  }
  if (includes(tiposPermitidos.value, tipo)) return true;
  const idx = TIPOS.indexOf(tipo);
  if (idx === -1) return false;
  return TIPOS.slice(idx + 1).some((t) => includes(tiposPermitidos.value, t));
}

function tieneHijos(item, tipoColumna) {
  const tipoItem = item.type || tipoColumna;
  const hijos = obtenerHijos({ ...item, type: tipoItem });
  return hijos.some((h) => esNavegableOSeleccionable(h));
}

function encontrarSeleccionEnItems(items) {
  const selectedIds = new Set(pathSeleccionado.value.map((node) => `${node.type}:${node.id}`));
  return items.find((item) => selectedIds.has(`${item.type}:${item.id}`));
}

function renderizarColumnas() {
  const columnasArray = [];
  let items = arbolEstructura.value;
  let tipo = 'anno_lectivo';

  while (!isEmpty(items)) {
    const tipoActual = tipo;
    const mostrarNivel = tipoActual !== 'nivel' || items.length > 1;

    if (tipoActual === 'nivel' && !mostrarNivel && items.length === 1) {
      const unico = items[0];
      const nodoNivel = { ...unico, type: 'nivel' };
      // Insertar nivel en la posición correcta del path (después de ensennanza, no al final)
      const pathSinNivel = pathSeleccionado.value.filter((node) => node.type !== 'nivel');
      const ensennanzaIdx = pathSinNivel.findIndex((n) => n.type === 'ensennanza');
      const insertAt = ensennanzaIdx !== -1 ? ensennanzaIdx + 1 : pathSinNivel.length;
      const nuevoPath = [...pathSinNivel];
      nuevoPath.splice(insertAt, 0, nodoNivel);
      pathSeleccionado.value = nuevoPath;

      items = obtenerHijos(unico).filter((item) => esNavegableOSeleccionable(item));
      tipo = tipoParaItems(items, tipoActual) || null;
      if (isEmpty(items)) break;
    } else {
      // Filtrar items para mostrar solo tipos navegables o seleccionables
      const itemsAMostrar = items.filter((item) => esNavegableOSeleccionable(item));
      if (isEmpty(itemsAMostrar)) break;

      let seleccionadoEnItems = encontrarSeleccionEnItems(itemsAMostrar);
      if (!seleccionadoEnItems) {
        const selectedIds = new Set(pathSeleccionado.value.map((node) => `${node.type}:${node.id}`));
        seleccionadoEnItems = itemsAMostrar.find((item) => item.__otrosGrupos && item.__otrosGrupos.some((child) => selectedIds.has(`${child.type}:${child.id}`)));
      }
      const selectedId = seleccionadoEnItems?.id || null;

      columnasArray.push({
        type: tipoActual,
        title: labels[tipoActual] || '',
        items: itemsAMostrar.map((item) => ({ ...item, conHijos: tieneHijos(item, tipoActual) })),
        selectedId,
      });

      if (!seleccionadoEnItems) break;

      const hijosRaw = obtenerHijos(seleccionadoEnItems);
      items = hijosRaw.filter((item) => esNavegableOSeleccionable(item));
      tipo = tipoParaItems(items, seleccionadoEnItems.type) || null;
    }
  }

  columnas.value = columnasArray;
}

function seleccionarNodo(item, tipo, _index) {
  const tipoNodo = item.type || tipo;

  // Usar el tipo del padre real (columna anterior) como punto de corte.
  // Esto permite seleccionar grupos en posiciones no estándar (ej. directamente bajo ensennanza),
  // así como nodos no estándar como "otros_grupos" que no están en TIPOS.
  const parentCol = columnas.value[_index - 1];
  const parentItem = parentCol?.items?.find((i) => i.id === parentCol.selectedId);
  const parentTipoIdx = parentItem?.type ? TIPOS.indexOf(parentItem.type) : -1;

  let pathFiltrado = [];
  if (parentTipoIdx !== -1) {
    const cutoff = parentTipoIdx + 1;
    pathFiltrado = pathSeleccionado.value.filter((n) => {
      const nIdx = TIPOS.indexOf(n.type);
      return nIdx !== -1 && nIdx < cutoff;
    });
  } else if (parentItem) {
    const parentIndex = pathSeleccionado.value.findIndex(
      (n) => n.type === parentItem.type && n.id === parentItem.id,
    );
    pathFiltrado = parentIndex !== -1
      ? pathSeleccionado.value.slice(0, parentIndex + 1)
      : pathSeleccionado.value.slice(0, _index);
  } else {
    pathFiltrado = pathSeleccionado.value.filter((n) => {
      const nIdx = TIPOS.indexOf(n.type);
      return nIdx !== -1 && nIdx < TIPOS.indexOf(tipoNodo);
    });
  }

  pathFiltrado.push({ ...item, type: tipoNodo });
  pathSeleccionado.value = pathFiltrado;
  renderizarColumnas();
}

async function cargarEstructura() {
  loading.value = true;
  error.value = null;
  try {
    const params = {
      current: props.soloAnnoActual,
      anno_lectivo_id: props.idAnnoLectivo,
      only_academic_groups: false,
      solo_cursos_lomloe: false,
      check_perms: false,
      add_grupos_profesor: true,
      add_grupos_hijos: false,
      incluir_extraescolares: false,
    };
    if (props.tiposEnsennanzaPermitidos?.length) {
      params.tipos_ensennanza = props.tiposEnsennanzaPermitidos.join(',');
    }
    const response = await estructuraEscolar.get({ params });
    arbolEstructura.value = response.data.annos_lectivos || [];
    if (props.valoresIniciales && !seleccionConfirmada.value.length) {
      const detalle = await estructuraEscolar.getDetalle(props.valoresIniciales);
      const pathInicial = construirPathFromHash(detalle.data || detalle);
      seleccionConfirmada.value = pathInicial;
      pathSeleccionado.value = pathInicial.slice();
    } else if (seleccionConfirmada.value.length) {
      pathSeleccionado.value = seleccionConfirmada.value.slice();
    }
    renderizarColumnas();
  } catch (catchedError) {
    error.value = catchedError;
  } finally {
    loading.value = false;
  }
}

function abrirModal() {
  if (props.disabled) return;
  if (seleccionConfirmada.value.length && !pathSeleccionado.value.length) {
    pathSeleccionado.value = seleccionConfirmada.value.slice();
  }
  renderizarColumnas();
  modalAbierto.value = true;
  if (!arbolEstructura.value.length && !loading.value) {
    cargarEstructura();
  }
}

function cerrarModal() {
  modalAbierto.value = false;
}

function dispatchChangeEvent({ tipo = null, idSeleccionado = null, path = [], inicial = false } = {}) {
  const wrapperElement = wrapper.value;
  if (!wrapperElement) return;

  // hay que hacerlo después de que se actualice el DOM con los nuevos inputs ocultos,
  // para que el listener del formulario los vea
  nextTick(() => {
    wrapperElement.dispatchEvent(new CustomEvent('change', {
      detail: { tipo, idSeleccionado, path, inicial },
      bubbles: true,
      cancelable: false,
    }));
  });
}

watch(seleccionConfirmada, (nuevoPath) => {
  const ultimo = nuevoPath?.[nuevoPath.length - 1] || null;
  dispatchChangeEvent({
    tipo: ultimo?.type ?? null,
    idSeleccionado: ultimo?.id ?? null,
    path: nuevoPath || [],
    inicial: !inicializado.value,
  });
});

function aceptarSeleccion() {
  const ultimo = ultimoNodoSeleccionable.value;
  if (!ultimo) return;
  const path = pathSeleccionado.value.slice();
  seleccionConfirmada.value = path;
  const tipo = ultimo.type;
  const idSeleccionado = ultimo.id;

  emit('seleccionada', {
    tipo,
    id: idSeleccionado,
    path,
  });
  cerrarModal();
}

function limpiarSeleccion() {
  console.log('Limpiando selección'); // eslint-disable-line no-console

  pathSeleccionado.value = [];
  seleccionConfirmada.value = [];
  renderizarColumnas();
  cerrarModal();
}

function getPathSeleccionado() {
  return seleccionConfirmada.value;
}

useEventListener(wrapper, 'limpiar-seleccion', limpiarSeleccion);

onMounted(() => {
  if (props.valoresIniciales) {
    cargarEstructura().finally(() => {
      inicializado.value = true;
    });
  } else {
    inicializado.value = true;
  }
});

defineExpose({
  limpiarSeleccion,
  getPathSeleccionado,
  getPathSeleccionadoEnHash,
});
</script>

<template>
  <div
    ref="wrapper"
    v-submit-on-change="submitAlCambiar"
    class="selector-estructura-wrapper"
  >
    <SelectorEstructuraDisplay
      :nodos-seleccion-mostrados="nodosSeleccionMostrados"
      :disabled="disabled"
      :loading="loading"
      @abrir="abrirModal"
    />

    <div class="selector-estructura-hidden-inputs">
      <input
        v-for="tipo in tiposPermitidos"
        :key="tipo"
        type="hidden"
        :name="nombreCampo(tipo) ?? undefined"
        :value="valoresCampos[tipo] ?? ''"
      />
    </div>

    <SelectorEstructuraModal
      v-model="modalAbierto"
      :loading="loading"
      :columnas="columnas"
      :puede-aceptar="puedeAceptar"
      :puede-limpiar="pathSeleccionado.length > 0"
      :nivel-modal="nivelModal"
      @seleccionar="({ item, tipo, index }) => seleccionarNodo(item, tipo, index)"
      @limpiar="limpiarSeleccion"
      @aceptar="aceptarSeleccion"
    />
  </div>
</template>

<style lang="scss" scoped>
.selector-estructura-wrapper {
  display: inline-flex;
  width: 100%;
}
</style>

<style lang="scss">
.modal-tamano-estructura {
  .modal-dialog {
    width: 85%;
    max-width: 1000px;
  }

  // Flex layout: header y footer siempre visibles, body scrollable
  .modal-content {
    display: flex;
    flex-direction: column;
  }

  .modal-body {
    flex: 1;
    min-height: 0;
    overflow: hidden !important;
    display: flex;
    flex-direction: column;
    padding-bottom: 0 !important;
  }
}
</style>
